// Core imports
import { Fragment, useEffect, useState, useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import 'moment-duration-format';

// Component imports
import { Col, DatePicker, Form, Row } from 'antd';
import AgentsPicker from '../Forms/Fields/AgentsPicker';
import CustomRangePicker from '../Forms/Fields/CustomRangePicker';

// Misc imports
import { DATETIME_BACKEND_FORMAT, getPresetDateRanges } from '../../constants/main';
import { setObservatorySearchParams, setHasVisitedObservatory } from '../../store/global/globalActions';
import { useFetchDashboardQuery } from '../../store/dashboard/dashboardApi';
import { isEmptyArray } from '../../utils/is/isEmptyArray';

const getDefaultStartDate = (timezone) => {
  return moment.tz(moment(), timezone).startOf('day');
};

const getDefaultEndDate = (timezone) => {
  return moment.tz(moment(), timezone).endOf('day');
};

export const useObservatorySearchParams = (timezone) => {
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const { observatorySearchParams: savedParams } = useSelector((state) => state.global);

  const startDateParam = searchParams.get('startDate') || savedParams.startDate;
  const endDateParam = searchParams.get('endDate') || savedParams.endDate;
  const tenantsFilterParam = searchParams.get('tenantsFilter') || savedParams.tenantsFilter;

  // Return object that can be spread directly into urlParams
  const observatorySearchParams = {};

  if (startDateParam) {
    if (!timezone) {
      observatorySearchParams.startDate = startDateParam || '';
    } else {
      observatorySearchParams.startDate = `${startDateParam}${encodeURIComponent(moment().tz(timezone)?.format('Z'))}` || '';
    }
  }

  if (endDateParam) {
    if (!timezone) {
      observatorySearchParams.endDate = endDateParam || '';
    } else {
      observatorySearchParams.endDate = `${endDateParam}${encodeURIComponent(moment().tz(timezone)?.format('Z'))}` || '';
    }
  }

  if (tenantsFilterParam && JSON.parse(decodeURIComponent(tenantsFilterParam))?.length > 0) {
    // normalize to single-encoded so apiSlice raw interpolation stays proxy-safe regradless of which producer set the URL
    observatorySearchParams.tenantsFilter = encodeURIComponent(decodeURIComponent(tenantsFilterParam));
  } else {
    observatorySearchParams.tenantsFilter = [];
  }

  return observatorySearchParams;
};

/* Get the observatory filters from the URL and convert them to initial form values
  * @param {string} timezone - The timezone to use for date parsing
  * @returns {object} - The observatory filters, which is:
  * {
  * startDate: moment object or undefined,
  * endDate: moment object or undefined,
  * tenantsFilter: array of tenant/deployment objects or empty array
  * }
*/
export const useObservatoryFilters = (timezone) => {
  const observatorySearchParams = useObservatorySearchParams(timezone);
  const startDateParam = observatorySearchParams.startDate;
  const endDateParam = observatorySearchParams.endDate;
  const tenantsFilterParam = observatorySearchParams.tenantsFilter;

  const emptyTenantsFilter = useMemo(() => [], []);
  const tenantsFilterParamKey = useMemo(() => {
    if (!tenantsFilterParam || Array.isArray(tenantsFilterParam)) {
      return '';
    }
    return tenantsFilterParam;
  }, [tenantsFilterParam]);

  const tenantsFilter = useMemo(() => {
    if (!tenantsFilterParamKey) {
      return emptyTenantsFilter;
    }
    try {
      return JSON.parse(decodeURIComponent(tenantsFilterParamKey));
    } catch (e) {
      return emptyTenantsFilter;
    }
  }, [tenantsFilterParamKey, emptyTenantsFilter]);

  return useMemo(() => {
    // Return object that can be spread directly into form initial values
    const observatoryFilters = {};
    if (startDateParam) {
      observatoryFilters.startDate = moment.tz(decodeURIComponent(startDateParam), DATETIME_BACKEND_FORMAT, timezone);
    } else {
      observatoryFilters.startDate = getDefaultStartDate(timezone);
    }

    if (endDateParam) {
      observatoryFilters.endDate = moment.tz(decodeURIComponent(endDateParam), DATETIME_BACKEND_FORMAT, timezone);
    } else {
      observatoryFilters.endDate = getDefaultEndDate(timezone);
    }

    observatoryFilters.tenantsFilter = tenantsFilter;
    return observatoryFilters;
  }, [startDateParam, endDateParam, timezone, tenantsFilter]);
};

export const useObservatoryTenantsFilterState = (observatorySearchParams) => {
  const { hasVisitedObservatory } = useSelector((state) => state.global);
  const hasTenantsFilter = useMemo(() => {
    const tenantsFilter = observatorySearchParams?.tenantsFilter;
    if (Array.isArray(tenantsFilter)) {
      return tenantsFilter.length > 0;
    }
    if (!tenantsFilter) {
      return false;
    }
    try {
      const decoded = JSON.parse(decodeURIComponent(tenantsFilter));
      return Array.isArray(decoded) ? decoded.length > 0 : Boolean(decoded?.length);
    } catch (error) {
      return false;
    }
  }, [observatorySearchParams?.tenantsFilter]);

  return {
    hasTenantsFilter,
    shouldSkipTenantsFilter: !hasTenantsFilter && !hasVisitedObservatory,
    hasVisitedObservatory
  };
};

export const useInitialTenantFilters = () => {
  const filters = useObservatoryFilters();
  const tenantsFilter = filters.tenantsFilter || [];
  const tenantsFilterKey = useMemo(() => {
    try {
      return JSON.stringify(tenantsFilter);
    } catch (error) {
      return String(Array.isArray(tenantsFilter) ? tenantsFilter.length : 0);
    }
  }, [tenantsFilter]);

  return useMemo(() => {
    return tenantsFilter.map(filter => {
      let checkedKeys = [];

      // If the whole tenant is selected (tenantId only - no deployments), add the tenantId to the checkedKeys
      if (filter.tenant && (!filter.deployments || isEmptyArray(filter.deployments)) && !filter.includeOtherDeployments) {
        checkedKeys.push(`${filter.tenant}`);
      }

      if (filter.includeOtherDeployments) {
        checkedKeys.push(`${filter.tenant}-includeOtherDeployments`);
      }

      if (filter.deployments?.length > 0) {
        filter.deployments.forEach(deployment => {
          checkedKeys.push(`${filter.tenant}-${deployment}`);
        });
      }

      return checkedKeys;
    }).flat();
  }, [tenantsFilterKey]);
};

const useUpdateObservatorySearchParams = (form, timezone) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const convertTenantFiltersToSearchParams = () => {
    const checkedKeys = form.getFieldValue('tenantsFilter') || [];
    const filtersToReturn = [];

    // Map to store tenants and their associated deployments
    const tenantMap = {};

    // Loop through the checked keys to organize tenant and deployment data
    checkedKeys.forEach((key) => {
      const [tenantId, deploymentId] = key.split('-');

      // If there's no deployment ID, this key represents a tenant selection
      if (!deploymentId) {
        if (!tenantMap[tenantId]) {
          tenantMap[tenantId] = {
            tenant: tenantId,
            deployments: [],
            includeOtherDeployments: false
          };
        }
      } else if (deploymentId === 'includeOtherDeployments') {
        // If 'includeOtherDeployments' is part of the key, mark it in the tenant object
        if (!tenantMap[tenantId]) {
          tenantMap[tenantId] = {
            tenant: tenantId,
            deployments: [],
            includeOtherDeployments: true
          };
        } else {
          tenantMap[tenantId].includeOtherDeployments = true;
        }
      } else {
        // Otherwise, it's a deployment selection
        if (!tenantMap[tenantId]) {
          tenantMap[tenantId] = {
            tenant: tenantId,
            deployments: [deploymentId],
            includeOtherDeployments: false
          };
        } else {
          tenantMap[tenantId].deployments.push(deploymentId);
        }
      }
    });

    // Convert the tenantMap into an array for use as a query parameter
    Object.keys(tenantMap).forEach((tenantId) => {
      filtersToReturn.push(tenantMap[tenantId]);
    });

    return filtersToReturn;
  };

  const updateObservatorySearchParams = () => {
    const searchParams = new URLSearchParams(window.location.search);

    let startDate = searchParams.get('startDate') ? moment(decodeURIComponent(searchParams.get('startDate')), DATETIME_BACKEND_FORMAT) : getDefaultStartDate(timezone);
    let endDate = searchParams.get('endDate') ? moment(decodeURIComponent(searchParams.get('endDate')), DATETIME_BACKEND_FORMAT) : getDefaultEndDate(timezone);
    let tenantsFilter = [];

    const rangePickerArray = form.getFieldValue('rangePicker') || [];
    if (rangePickerArray.length === 2) {
      startDate = rangePickerArray[0];
      endDate = rangePickerArray[1];
    }
    tenantsFilter = convertTenantFiltersToSearchParams();
    if (startDate) {
      const plainDateString = startDate.format(DATETIME_BACKEND_FORMAT);
      searchParams.set('startDate', encodeURIComponent(`${plainDateString}`));
    } else {
      searchParams.delete('startDate');
    }

    if (endDate) {
      const plainDateString = endDate.format(DATETIME_BACKEND_FORMAT);
      searchParams.set('endDate', encodeURIComponent(`${plainDateString}`));
    } else {
      searchParams.delete('endDate');
    }

    searchParams.set('tenantsFilter', encodeURIComponent(JSON.stringify(tenantsFilter)));

    // Keep the new params in redux for future use
    dispatch(setObservatorySearchParams({
      startDate: searchParams.get('startDate'),
      endDate: searchParams.get('endDate'),
      tenantsFilter: searchParams.get('tenantsFilter')
    }));

    // Set the filters to the url
    navigate({
      pathname: location.pathname,
      search: searchParams.toString()
    }, { replace: true });
  };

  return { updateObservatorySearchParams };
};

const ObservatoryFiltersRow = ({
  form,
  timezone = 'UTC',
  rangePickerProps = {},
  agentsPickerProps = {},
  showDivider = false
}) => {
  const { t } = useTranslation();
  const { location } = useLocation();

  const dispatch = useDispatch();

  const searchParams = useMemo(() => new URLSearchParams(location?.search), [location?.search]);
  const { observatorySearchParams: savedParams } = useSelector((state) => state.global);

  const [shouldUseMostUsedTenant, setShouldUseMostUsedTenant] = useState(false);

  const { hasVisitedObservatory } = useSelector((state) => state.global);
  const { updateObservatorySearchParams } = useUpdateObservatorySearchParams(form, timezone);

  const { orgId } = useParams();
  const { recentBots } = useSelector((state) => state.bots);

  const { data: dashboard } = useFetchDashboardQuery(
    { orgId },
    { skip: !shouldUseMostUsedTenant || !orgId }

  );

  const checkAndSelectBotInitially = useCallback(() => {
    // We only need that to run initially
    if (hasVisitedObservatory) {
      return;
    }
    // Read current tenantsFilter from the URL or savedParams (redux store)
    const tenantsFilterParam = searchParams.get('tenantsFilter') || savedParams.tenantsFilter;
    let tenantsFilterParamDecoded = [];
    if (tenantsFilterParam) {
      try {
        tenantsFilterParamDecoded = JSON.parse(decodeURIComponent(tenantsFilterParam));
      } catch (error) {
        tenantsFilterParamDecoded = [];
      }
    }
    // Check if there is a tenantsFilter parameter in the URL
    const hasTenantsFilterParam = Array.isArray(tenantsFilterParamDecoded)
      ? tenantsFilterParamDecoded.length > 0
      : Boolean(tenantsFilterParamDecoded && tenantsFilterParamDecoded.length > 0);

    // If there is a tenantsFilter parameter in the URL, we don't need to select a bot initially
    if (hasTenantsFilterParam) {
      return;
    }

    // Get the most recent bot from the recentBots list
    const recentBot = recentBots?.[0];

    setShouldUseMostUsedTenant(!recentBot);

    const agentsList = agentsPickerProps.agentsList || [];
    const shouldIncludeDeployments = agentsPickerProps.showDeployments !== false;
    const markVisited = () => dispatch(setHasVisitedObservatory());

    const buildTenantFilterKeys = (agent) => {
      if (!agent) {
        return [];
      }
      const filterKeys = [agent.id];
      if (shouldIncludeDeployments && agent.botDeployments?.length > 0) {
        agent.botDeployments.forEach((deployment) => {
          filterKeys.push(`${agent.id}-${deployment.id}`);
        });
        filterKeys.push(`${agent.id}-includeOtherDeployments`);
      }
      return filterKeys;
    };

    const applyTenantFilter = (filterKeys) => {
      if (filterKeys.length > 0) {
        form.setFieldsValue({ tenantsFilter: filterKeys });
        updateObservatorySearchParams();
      }
      markVisited();
    };

    // If we found a recent bot just use it and leave
    if (recentBot) {
      const recentBotFromOptions = agentsList.find(agent => agent.id === recentBot.id);
      applyTenantFilter(buildTenantFilterKeys(recentBotFromOptions));
      return;
    }

    // Didn't find a recent bot -- lets go for most used if one
    const mostUsedTenant = dashboard?.mostUsedTenants?.[0];

    // if most used tenant exists, use it and leave
    if (mostUsedTenant) {
      const mostUsedBotFromOptions = agentsList.find(agent => agent.id === mostUsedTenant.tenantId);
      applyTenantFilter(buildTenantFilterKeys(mostUsedBotFromOptions));
      return;
    }

    // Didn't find most used (neither most recent)
    if (dashboard && dashboard.mostUsedTenants && dashboard.mostUsedTenants.length > 0) {
      markVisited();
    }

  }, [
    agentsPickerProps.agentsList,
    agentsPickerProps.showDeployments,
    dashboard?.mostUsedTenants,
    dispatch,
    form,
    hasVisitedObservatory,
    recentBots,
    savedParams.tenantsFilter,
    searchParams,
    updateObservatorySearchParams
  ]);

  useEffect(() => {
    if (agentsPickerProps?.agentsList.length > 0) {
      checkAndSelectBotInitially();
    }
  }, [agentsPickerProps?.agentsList, checkAndSelectBotInitially]);
  // On mount
  useEffect(() => {

    // Timeout is necessary to ensure other url params are set first
    setTimeout(() => {
      // Mark observatory as visited on first mount
      updateObservatorySearchParams();
    }, 0);
  }, []);

  return (
    (rangePickerProps.hidden && agentsPickerProps.hidden) ? null : (
      <Fragment>
        <Row gutter={[16, 16]} className="mb-4">
          {
            rangePickerProps.hidden ? null : (
              <Col flex="none">
                {
                  rangePickerProps.type === 'week' ? (
                    <Fragment>
                      <Form.Item
                        name="weekPicker"
                        initialValue={form.getFieldValue('endDate') ? moment(form.getFieldValue('endDate')) : moment().endOf('week')}
                        normalize={value => {
                          // We want to keep the end of the week as our true value
                          return moment(value).endOf('week');
                        }}
                        noStyle
                      >
                        <DatePicker
                          picker="week"
                          size={rangePickerProps.size || 'large'}
                          allowClear={rangePickerProps.allowClear || false}
                          style={rangePickerProps.style || { width: 351 }} // To match the width of the CustomRangePicker
                          disabledDate={rangePickerProps.disabledDate || ((current) => {
                            // Disable future weeks
                            return moment(current).startOf('week').isAfter(moment());
                          })}
                          format={rangePickerProps.format || ((value) => {
                            // Still display the week range as before
                            const weekStart = moment(value).startOf('week');
                            const weekEnd = moment(value).endOf('week');
                            return `${weekStart.format('DD MMM YYYY')} - ${weekEnd.format('DD MMM YYYY')}`;
                          })}
                          onChange={(date) => {
                            // Cloning is necessary to avoid mutating the original date object
                            const startDate = date ? date.clone().startOf('week') : null;
                            const endDate = date ? date.clone().endOf('week') : null;

                            form.setFieldsValue({ rangePicker: date ? [
                              startDate,
                              endDate
                            ] : null });

                            updateObservatorySearchParams();
                          }}
                          {...rangePickerProps}
                        />
                      </Form.Item>

                      <Form.Item
                        name="rangePicker"
                        hidden
                      >
                        <input type="hidden" />
                      </Form.Item>
                    </Fragment>
                  ): (
                    <Form.Item
                      name="rangePicker"
                      rules={[{
                        required: false,
                        message: t('Please select a date range.')
                      }]}
                      noStyle
                    >
                      <CustomRangePicker
                        size={rangePickerProps.size || 'large'}
                        disabled={rangePickerProps.disabled || false}
                        allowClear={rangePickerProps.allowClear || false}
                        disabledDate={rangePickerProps.disabledDate || ((current) => {
                          return current && current > getDefaultEndDate(timezone);
                        })}
                        ranges={rangePickerProps.ranges || getPresetDateRanges(timezone) || {}}
                        timezone={timezone}
                        onChange={(dates) => {
                          if (dates === null) {
                            form.setFieldsValue({ rangePicker: [
                              getDefaultStartDate(timezone),
                              getDefaultEndDate(timezone)
                            ] });
                          } else {
                            form.setFieldsValue({ rangePicker: [
                              dates[0],
                              dates[1]
                            ] });
                          }

                          updateObservatorySearchParams();
                        }}
                        {...rangePickerProps}
                      />
                    </Form.Item>
                  )
                }
              </Col>
            )
          }

          {
            agentsPickerProps.hidden ? null : (
              <Col flex="none">
                <Form.Item
                  name="tenantsFilter"
                  noStyle
                >
                  <AgentsPicker
                    agentsList={agentsPickerProps.agentsList || []}
                    onChange={updateObservatorySearchParams}
                    {...agentsPickerProps}
                  />
                </Form.Item>
              </Col>
            )}
        </Row>

        {showDivider && <div className="gradient-border mb-4" />}
      </Fragment>
    )
  );
};

ObservatoryFiltersRow.propTypes = {
  form: PropTypes.object.isRequired,
  timezone: PropTypes.string,
  rangePickerProps: PropTypes.object,
  agentsPickerProps: PropTypes.object,
  showDivider: PropTypes.bool
};

export default ObservatoryFiltersRow;
